import { useEffect, useState } from "react";
import { cloudAuthDesktopV2 } from "../../services/cloudAuthDesktopV2";
import { getWebApiBaseUrl } from "../../lib/runtime";
import { CRAFT_AREA_LABELS, type StoryCraftAreaId } from "../../../shared/craftAreas";
import { Button } from "../ui/button";

type ResourceHit = {
  resourceId: string;
  resourceTitle: string;
  resourceType: string;
  hostOrAuthor: string | null;
  url: string;
};

export function ImproveThisStrip({ area }: { area: StoryCraftAreaId }) {
  const [resources, setResources] = useState<ResourceHit[] | null>(null);
  const [practicedBusy, setPracticedBusy] = useState(false);
  const [practicedOk, setPracticedOk] = useState(false);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        if (!cloudAuthDesktopV2.isSignedIn()) {
          if (!cancelled) setResources([]);
          return;
        }
        const token = await cloudAuthDesktopV2.getValidAccessToken().catch(() => null);
        if (!token) {
          if (!cancelled) setResources([]);
          return;
        }
        const base = getWebApiBaseUrl();
        const res = await fetch(
          `${base}/api/writing-resources/by-area?area=${encodeURIComponent(area)}&limit=3`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        if (!res.ok) {
          if (!cancelled) setResources([]);
          return;
        }
        const data = (await res.json()) as { resources?: ResourceHit[] };
        if (!cancelled) setResources(data.resources ?? []);
      } catch {
        if (!cancelled) setResources([]);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [area]);

  const markPracticed = async () => {
    setPracticedBusy(true);
    try {
      const token = await cloudAuthDesktopV2.getValidAccessToken().catch(() => null);
      if (!token) return;
      const base = getWebApiBaseUrl();
      const res = await fetch(`${base}/api/learn/progress`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ craftArea: area, note: "Practiced from Improve this" }),
      });
      if (res.ok) setPracticedOk(true);
    } finally {
      setPracticedBusy(false);
    }
  };

  if (!resources || resources.length === 0) return null;

  return (
    <aside className="mt-2 rounded-md border border-dashed border-border/80 bg-muted/10 px-2 py-1.5">
      <p className="mb-1 text-[0.6875rem] font-semibold uppercase tracking-wide text-muted-foreground">
        Improve this · {CRAFT_AREA_LABELS[area]}
      </p>
      <ul className="m-0 list-none space-y-0.5 p-0">
        {resources.map((r) => (
          <li key={`${r.resourceId}-${r.url}`} className="text-xs leading-snug">
            <Button asChild variant="link" size="xs" className="h-auto p-0 text-xs">
              <a href={r.url} target="_blank" rel="noreferrer">
                {r.resourceTitle}
                {r.hostOrAuthor ? ` — ${r.hostOrAuthor}` : ""}
              </a>
            </Button>
            <span className="text-muted-foreground"> · {r.resourceType}</span>
          </li>
        ))}
      </ul>
      <div className="mt-1.5">
        <Button
          type="button"
          size="xs"
          variant="outline"
          disabled={practicedBusy || practicedOk}
          onClick={() => void markPracticed()}
        >
          {practicedOk ? "Marked practiced" : practicedBusy ? "Saving…" : "Mark practiced"}
        </Button>
      </div>
    </aside>
  );
}
